import type { FC } from 'react';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import { Stack, Typography } from '@mui/material';

import { LANGUAGE_MAP } from 'src/constants/language';
import ButtonPanel from 'src/components/common/ButtonPanel';
import Modal from 'src/components/common/Modal';
import { useShoppingCart } from 'src/components/report/ShoppingCartContext';
import { useAvailableLanguages } from 'src/components/reportConfig/hooks/useAvailableLanguages';

import LanguageSelection from './LanguageSelection';

import { styles } from './index.styles';

export const LANGUAGE_SELECTOR_FIELD_TEST_ID = 'language-selector-field';

const LanguageSelectorField: FC = () => {
  const { shoppingCartLanguage, setShoppingCartLanguage } = useShoppingCart();
  const { t, i18n } = useTranslation(['report', 'navigation']);
  const [modalOpen, setModalOpen] = useState(false);
  const [userChangedLanguage, setUserChangedLanguage] = useState(false);
  const toggleModal = () => {
    setModalOpen((prev) => !prev);
  };
  // If user changes the language in the modal,
  // we don't want to update our language
  const handleUserChange = () => {
    setUserChangedLanguage(true);
  };

  const { shoppingCartLanguages, languageNamesPerCode, loading } =
    useAvailableLanguages() || [];

  useEffect(() => {
    const defaultLanguageMap = shoppingCartLanguages ?? LANGUAGE_MAP;

    // Find initial language based on browser language
    const initialLanguage = defaultLanguageMap.find(
      (e) => e.abbreviation === i18n.language,
    );
    // As long as the user hasn't changed the language in the modal
    // we show the browser language
    if (!userChangedLanguage) {
      setShoppingCartLanguage(initialLanguage || defaultLanguageMap[0]);
    }
  }, [setShoppingCartLanguage, userChangedLanguage, loading]);

  return (
    <Fragment>
      <ButtonPanel
        dataTestId={LANGUAGE_SELECTOR_FIELD_TEST_ID}
        onClick={toggleModal}
      >
        <LanguageRounded fontSize="large" />
        <Stack direction="column" spacing={0.5} sx={styles.title}>
          <Typography>{t('shoppingCart.languageTitle')}</Typography>
          <Typography sx={styles.selectedLanguage}>
            {shoppingCartLanguage ? (
              <>
                {languageNamesPerCode?.[shoppingCartLanguage?.code] ??
                  t(
                    `navigation:language.${shoppingCartLanguage.abbreviation}`,
                  )}{' '}
                ({shoppingCartLanguage.abbreviation.toUpperCase()})
              </>
            ) : (
              '-'
            )}
          </Typography>
        </Stack>
      </ButtonPanel>
      <Modal
        isOpen={modalOpen}
        onClose={toggleModal}
        headline={t('shoppingCart.languageSelect')}
      >
        <LanguageSelection
          onClose={toggleModal}
          handleUserChange={handleUserChange}
        />
      </Modal>
    </Fragment>
  );
};

export default LanguageSelectorField;
